// The composed Content-tab read (content) + the AI tools + "Content I'm trying" saved
// ideas. ONE band-scoped query feeds the social command center, keyed ['band', bandId, 'content-stats'] so a band
// switch scorches it AND the `sync-completed` plane-B signal invalidates it live. Served straight from the
// analytics.* projections — NO vendor call on this read path. The AI tools are USER-INITIATED fast jobs
// (POST); they are NOT on the read path, are per-user rate-limited server-side (429), and provider-guarded (502 →
// the tile shows "try again"). Saved ideas persist per band; their mutations invalidate the saved-ideas query.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type ContentView = components["schemas"]["ContentView"];
export type ContentPlatform = components["schemas"]["ContentPlatform"];
export type ReachHero = components["schemas"]["ReachHero"];
export type ContentPostRow = components["schemas"]["ContentPostRow"];
export type UgcTrackRow = components["schemas"]["UgcTrackRow"];
export type HashtagResult = components["schemas"]["HashtagResult"];
export type IdeasResult = components["schemas"]["IdeasResult"];
export type ContentIdea = components["schemas"]["ContentIdea"];
export type PostStats = components["schemas"]["PostStats"];
export type PostAnalysis = components["schemas"]["PostAnalysis"];
export type PostAnalysisJob = components["schemas"]["PostAnalysisJob"];
export type RetentionPoint = components["schemas"]["RetentionPoint"];
export type SavedIdeasList = components["schemas"]["SavedIdeasList"];
export type SavedIdeaView = components["schemas"]["SavedIdeaView"];

const SAVED_IDEAS_KEY = ["content", "saved-ideas"] as const;

/** GET the composed Content tab (releases:read). `staleTime` ~60s per the stats domain.*/
export function useContentStats() {
  const { api } = useApp();
  return useBandQuery<ContentView>(
    ["content-stats"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/content", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 },
  );
}

/** GET the band's saved ideas ("Content I'm trying", releases:read). */
export function useSavedIdeas() {
  const { api } = useApp();
  return useBandQuery<SavedIdeasList>(
    SAVED_IDEAS_KEY,
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/content/saved-ideas", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 },
  );
}

// ---- AI tools (one-shot fast-job mutations; 429/502 surface as ApiError to the "try again" UI) ---------

export function useGenerateHashtags() {
  const { api } = useApp();
  return useBandMutation<HashtagResult, { platform: string | null; description: string }>(
    async (bandId, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/content/hashtags", {
        params: { path: { bandId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
  );
}

export function useGenerateIdeas() {
  const { api } = useApp();
  return useBandMutation<IdeasResult, { platform: string | null; offset?: number }>(
    async (bandId, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/content/ai/ideas", {
        params: { path: { bandId } },
        body: { platform: body.platform, offset: body.offset ?? 0 },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
  );
}

// ---- "Get the data" (ART-262): one of the band's OWN posts, by pasted link -----------------------------------
// Stats come back at once (live Graph/TikTok on the server — a POST because it calls vendors, never a page-load
// read). The analysis is a JOB: start returns it with the stats, then the job is polled until it settles.

/** POST post-stats — 404 not the band's post · 409 not connected · 422 bad link · 429 cap. */
export function usePostStats() {
  const { api } = useApp();
  return useBandMutation<PostStats, { url: string }>(async (bandId, body) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/content/post-stats", {
      params: { path: { bandId } },
      body,
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  });
}

/** POST post-analysis — starts the job (Gemini watches the post in the worker). `mediaId` is a band-uploaded MP4. */
export function useStartPostAnalysis() {
  const { api } = useApp();
  return useBandMutation<PostAnalysisJob, { url: string; mediaId?: string | null }>(async (bandId, body) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/content/post-analysis", {
      params: { path: { bandId } },
      body,
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  });
}

/** A job is settled once it is done or failed; nothing changes after that. */
export function isJobSettled(job: PostAnalysisJob | undefined | null): boolean {
  return job?.status === "done" || job?.status === "failed";
}

/** GET post-analysis/{jobId}, polled every 3 s until the job settles. */
export function usePostAnalysisJob(jobId: string | null) {
  const { api } = useApp();
  return useBandQuery<PostAnalysisJob>(
    ["content", "post-analysis", jobId],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/content/post-analysis/{jobId}", {
        params: { path: { bandId, jobId: jobId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      enabled: jobId != null,
      refetchInterval: (q) => (isJobSettled(q.state.data) || q.state.error ? false : 3000),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

// ---- "Content I'm trying" saved ideas (persist per band; mutations invalidate the saved-ideas query) ----

export function useSaveIdea() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<SavedIdeaView, { platform: string | null; title: string | null; body: string | null }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/content/saved-ideas", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...SAVED_IDEAS_KEY] }) },
  );
}

export function useUpdateIdeaStatus() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<SavedIdeaView, { ideaId: string; status: "trying" | "done" | "dropped" }>(
    async (band, { ideaId, status }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/content/saved-ideas/{ideaId}", {
        params: { path: { bandId: band, ideaId } },
        body: { status },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...SAVED_IDEAS_KEY] }) },
  );
}

export function useDeleteIdea() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<void, string>(
    async (band, ideaId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/content/saved-ideas/{ideaId}", {
        params: { path: { bandId: band, ideaId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...SAVED_IDEAS_KEY] }) },
  );
}

export function useClearIdeas() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<void, void>(
    async (band) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/content/saved-ideas", {
        params: { path: { bandId: band } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...SAVED_IDEAS_KEY] }) },
  );
}
